import { useState } from 'react';
import { Check, X, AlertCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { QuestionItem } from '@/types/api.types';

export interface QuestionExtractorProps {
  questions: QuestionItem[];
  onConfirm: (selectedQuestions: QuestionItem[]) => void;
  onCancel: () => void;
  isProcessing?: boolean;
  isConfirmDisabled?: boolean;
  confirmDisabledMessage?: string;
}

export function QuestionExtractor({
  questions,
  onConfirm,
  onCancel,
  isProcessing = false,
  isConfirmDisabled = false,
  confirmDisabledMessage,
}: QuestionExtractorProps) {
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());

  const selectedQuestions = questions.filter(
    (q) => !excludedIds.has(q.question_id)
  );
  const selectedCount = selectedQuestions.length;

  const toggleQuestion = (questionId: string) => {
    setExcludedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setExcludedIds(new Set());
  };

  const deselectAll = () => {
    setExcludedIds(new Set(questions.map((q) => q.question_id)));
  };

  const handleConfirm = () => {
    onConfirm(selectedQuestions);
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Extracted Questions</CardTitle>
          </div>
          <div className="text-sm text-muted-foreground">
            {selectedCount} of {questions.length} selected
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Selection controls */}
        <div className="flex items-center gap-2 pb-2 border-b">
          <Button
            variant="ghost"
            size="sm"
            onClick={selectAll}
            disabled={isProcessing || excludedIds.size === 0}
          >
            Select All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={deselectAll}
            disabled={isProcessing || excludedIds.size === questions.length}
          >
            Deselect All
          </Button>
        </div>

        {/* Questions list */}
        <div className="max-h-[400px] overflow-y-auto space-y-2">
          {questions.length === 0 ? (
            <div className="flex items-center gap-2 p-4 text-muted-foreground bg-muted/50 rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <span>No questions were extracted from the file.</span>
            </div>
          ) : (
            questions.map((question, index) => {
              const isExcluded = excludedIds.has(question.question_id);
              return (
                <div
                  key={question.question_id}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer',
                    isExcluded
                      ? 'bg-muted/30 border-muted text-muted-foreground'
                      : 'bg-background border-border hover:bg-muted/50'
                  )}
                  onClick={() => !isProcessing && toggleQuestion(question.question_id)}
                >
                  <div className="pt-0.5">
                    <input
                      type="checkbox"
                      checked={!isExcluded}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleQuestion(question.question_id);
                      }}
                      disabled={isProcessing}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-mono text-muted-foreground mr-2">
                      {index + 1}.
                    </span>
                    <span className={cn(isExcluded && 'line-through')}>
                      {question.question}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Info message */}
        {questions.length > 0 && excludedIds.size > 0 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {excludedIds.size} question{excludedIds.size !== 1 ? 's' : ''} will be
              excluded from processing.
            </span>
          </div>
        )}

        {confirmDisabledMessage && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{confirmDisabledMessage}</span>
          </div>
        )}

        {/* Action buttons — relative + z-10 to ensure no overlap with scrollable question list */}
        <div className="relative z-10 flex justify-end gap-3 pt-4 mt-2 border-t">
          <Button variant="outline" onClick={onCancel} disabled={isProcessing}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={(e) => { e.stopPropagation(); handleConfirm(); }}
            disabled={isProcessing || selectedCount === 0 || isConfirmDisabled}
          >
            <Check className="h-4 w-4 mr-2" />
            {isProcessing
              ? 'Processing...'
              : `Process ${selectedCount} Question${selectedCount !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
